// Thin client for the CMS API. Same origin, so the session cookie rides along
// and the Worker's Origin check passes without any CORS.

import type { ContentType, PublicUser } from '@mlopsmena/content'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues: { path: string; message: string }[] = [],
  ) {
    super(message)
  }
}

async function request<T>(method: string, path: string, body?: unknown, raw?: Blob): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: raw ? { 'content-type': raw.type } : body !== undefined ? { 'content-type': 'application/json' } : {},
    body: raw ?? (body !== undefined ? JSON.stringify(body) : undefined),
  })
  if (res.status === 204) return undefined as T
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const issues = Array.isArray(data.issues) ? data.issues : []
    const message =
      data.message ??
      (issues.length ? issues.map((i: { path: string; message: string }) => `${i.path}: ${i.message}`).join('\n') : null) ??
      `${res.status} ${data.error ?? res.statusText}`
    throw new ApiError(res.status, data.error ?? 'error', message, issues)
  }
  return data as T
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  del: (path: string) => request<void>('DELETE', path),
  upload: <T>(path: string, blob: Blob) => request<T>('POST', path, undefined, blob),
}

/* ---------- shapes the API returns ---------- */

export type Me = PublicUser

/** A content item as the API returns it; fields vary by type. */
export type Item = Record<string, unknown> & {
  id: string
  slug: string
  status: 'draft' | 'published' | 'archived'
  sortOrder: number
  updatedAt: string
}

export type MediaItem = {
  key: string
  content_type: string
  bytes: number
  original_name: string | null
  uploaded_at: string
}

export type Publish = {
  id: string
  status: 'queued' | 'building' | 'success' | 'failed' | 'dispatch_failed'
  triggeredBy: string | null
  runUrl: string | null
  message: string | null
  createdAt: string
  updatedAt: string
}

export type PublishStatus = {
  last: Publish | null
  lastSuccessAt: string | null
  changed: Partial<Record<ContentType | 'settings', number>>
}
